// components/IDE/ClassInitArgsDialog.tsx
import React, { useState, useEffect } from 'react';

interface ClassInitArgsDialogProps {
    isOpen: boolean;
    onClose: () => void;
    onSubmit: (initArgs: string) => void;
    actorClassName: string;
    initParams: string;
    deploymentMode?: string; // Add deployment mode parameter
}

const ClassInitArgsDialog: React.FC<ClassInitArgsDialogProps> = ({
                                                                     isOpen,
                                                                     onClose,
                                                                     onSubmit,
                                                                     actorClassName,
                                                                     initParams,
                                                                     deploymentMode = 'upgrade' // Default to 'upgrade' if not specified
                                                                 }) => {
    const [initArgValue, setInitArgValue] = useState('()');

    useEffect(() => {
        if (isOpen) {
            // Reset value when dialog opens
            setInitArgValue('()');
        }
    }, [isOpen]);

    if (!isOpen) return null;

    // Helper function to get a display name for the deployment mode
    const getModeDisplayName = (mode: string) => {
        switch (mode) {
            case 'upgrade':
                return 'Upgrade (EOP)';
            case 'install':
                return 'Install';
            case 'reinstall':
                return 'Reinstall';
            default:
                return mode;
        }
    };

    // Determine if this is a destructive mode that needs a warning
    const isDestructiveMode = deploymentMode === 'reinstall';

    return (
        <div style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000
        }}>
            <div style={{
                backgroundColor: "#fdf6e3",
                padding: "24px",
                borderRadius: "8px",
                width: "500px",
                maxWidth: "90%",
                boxShadow: "0 4px 12px rgba(0,0,0,0.15)"
            }}>
                <h3 style={{
                    margin: "0 0 16px 0",
                    color: "#5C4033",
                    fontSize: "24px",
                    fontWeight: "500"
                }}>
                    Class-Based Actor Initialization
                </h3>

                {/* Deployment Mode Badge */}
                <div style={{
                    display: "flex",
                    alignItems: "center",
                    marginBottom: "16px",
                    padding: "8px 12px",
                    backgroundColor: isDestructiveMode ? "#fff3f0" : "#f3e5d8",
                    borderRadius: "4px",
                    borderLeft: `4px solid ${isDestructiveMode ? "#d32f2f" : "#8B4513"}`
                }}>
                    <span style={{
                        fontWeight: "bold",
                        marginRight: "8px",
                        color: "#5C4033"
                    }}>
                        Deployment Mode:
                    </span>
                    <span style={{
                        color: isDestructiveMode ? '#d32f2f' : '#4B8B3B',
                        fontWeight: isDestructiveMode ? 'bold' : 'normal'
                    }}>
                        {getModeDisplayName(deploymentMode)}
                    </span>

                    {isDestructiveMode && (
                        <span style={{
                            marginLeft: "auto",
                            color: "#d32f2f",
                            fontSize: "12px",
                            fontStyle: "italic"
                        }}>
                            Warning: Will erase all canister state!
                        </span>
                    )}
                </div>

                <p style={{
                    margin: "0 0 12px 0",
                    color: "#8B4513",
                    fontSize: "16px"
                }}>
                    Your actor requires initialization arguments:
                </p>
                <code style={{
                    display: "block",
                    margin: "0 0 24px 0",
                    padding: "12px",
                    backgroundColor: "#f3e5d8",
                    color: "#5C4033",
                    fontSize: "14px",
                    fontFamily: "monospace",
                    whiteSpace: "pre-wrap",
                    borderRadius: "4px"
                }}>
                    actor class {actorClassName}({initParams})
                </code>
                <div style={{marginBottom: "20px"}}>
                    <label style={{
                        display: "block",
                        marginBottom: "8px",
                        color: "#5C4033",
                        fontSize: "14px"
                    }}>
                        Enter initialization arguments in Candid format:
                    </label>
                    <input
                        type="text"
                        value={initArgValue}
                        onChange={(e) => setInitArgValue(e.target.value)}
                        placeholder="(42)"
                        style={{
                            width: "100%",
                            padding: "8px",
                            borderRadius: "4px",
                            border: "1px solid #8B4513",
                            backgroundColor: "#fff",
                            fontSize: "14px",
                            boxSizing: "border-box"
                        }}
                    />
                </div>
                <p style={{
                    margin: "0 0 20px 0",
                    color: "#8B4513",
                    fontSize: "14px"
                }}>
                    <strong>Examples:</strong><br />
                    - For numbers: (42)<br />
                    - For text: ("Hello")<br />
                    - For records: (record {'{'} name = "Alice"; age = 30 {'}'})<br />
                    - For arrays: (vec {'{'} 1; 2; 3 {'}'})<br />
                    - For empty/no arguments: ()
                </p>
                <div style={{display: "flex", gap: "12px", justifyContent: "flex-end"}}>
                    <button
                        onClick={onClose}
                        style={{
                            padding: "8px 16px",
                            backgroundColor: "#8B4513",
                            color: "#FFF5EE",
                            border: "none",
                            borderRadius: "4px",
                            cursor: "pointer",
                            fontWeight: "500",
                            fontSize: "14px"
                        }}
                    >
                        Cancel
                    </button>
                    <button
                        onClick={() => {
                            console.log("Raw value in dialog before submission:", initArgValue);
                            onSubmit(initArgValue);
                        }}
                        style={{
                            padding: "8px 16px",
                            backgroundColor: isDestructiveMode ? "#c62828" : "#5C4033",
                            color: "#FFF5EE",
                            border: "none",
                            borderRadius: "4px",
                            cursor: "pointer",
                            fontWeight: "500",
                            fontSize: "14px"
                        }}
                    >
                        {isDestructiveMode ? 'Deploy (Reset State)' : 'Deploy'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ClassInitArgsDialog;